import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import Collapsible from "@shared/components/ui-kit/disclosure/Collapsible/Collapsible.web";
import Button from "@shared/components/ui-kit/form/Button/Button.web";
import Icon from "@shared/components/ui-kit/icons/Icon.web";
import Box from "@shared/components/ui-kit/layout/Box/Box.web";
import { UserBasedDiscountTicketInfoSerializer } from "@shared/modules/discounts/types";
import usePublicBasedVenuesService from "@shared/modules/venue/services/usePublicBasedVenuesService";
import { useTranslation } from "@shared/utils/internationalization";
import Tile from "@ui-kit/form/Tile/Tile.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useEffect, useMemo, useState } from "react";
import usePublicApplicableTicketCredits from "./hooks/usePublicApplicableTicketCredits";

export default function ApplicableTicketCredits() {
  const { t } = useTranslation("checkout");
  const [isRestOfTicketCreditsExpanded, setIsRestOfTicketCreditsExpanded] =
    useState(false);

  const {
    relevantTicketCredits,
    ticketCreditsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePublicApplicableTicketCredits();

  const { hasDiscountsApplied, totalPrice, isWaitlist, paymentVenue } =
    usePublicBasket();

  const { useRetrieve } = usePublicBasedVenuesService();

  const { data: venueData } = useRetrieve(paymentVenue);

  const allowMultiDiscounts = venueData?.allow_multi_discounts;

  const applicableTicketCredits = useMemo(() => {
    return relevantTicketCredits.filter(
      (tc) => tc.stats?.discounts?.available > 0
    );
  }, [relevantTicketCredits]);

  useEffect(() => {
    // if we havent loaded any applicable ticket credit, and there are more pages to load, fetch the next page
    if (hasNextPage && !applicableTicketCredits.length) {
      fetchNextPage();
    }
  }, [applicableTicketCredits, hasNextPage, fetchNextPage]);

  // Early return if still loading
  if (ticketCreditsLoading) {
    return null;
  }

  const shouldShowTicketCredits =
    applicableTicketCredits.length > 0 &&
    parseFloat(totalPrice) > 0 &&
    !isWaitlist &&
    (allowMultiDiscounts || !hasDiscountsApplied);

  if (!shouldShowTicketCredits) {
    return null;
  }

  return (
    <>
      <Flex gap="md" direction="column">
        <Box>
          <Text fontSize="xl" fontWeight="bold">
            {t("Available credit")}
          </Text>
          <Text fontSize="md" color="gray.600">
            {t(
              "Looks like you have some ticket credits you can apply to this basket!"
            )}
          </Text>
        </Box>
        <Flex flexDirection="row" flexWrap="wrap" gap="md">
          {applicableTicketCredits.slice(0, 2).map((tc) => {
            return (
              <Box
                key={tc.code}
                // Force 2 column layout; 50% of container - gap
                flex="1 1 calc(50% - 16px)"
                maxWidth="calc(50% - 8px)"
                sx={{
                  // Force full width if there is only one option
                  "&:only-child": {
                    flex: "1 1 100%",
                    maxWidth: "100%",
                  },
                }}
              >
                <ApplicableTicketCredits.TicketCredit ticketCredit={tc} />
              </Box>
            );
          })}
        </Flex>

        <Collapsible
          in={isRestOfTicketCreditsExpanded}
          startingHeight="0.01px" // Prevent stutter when collapsing
          style={{
            margin: "-1px", // Allow outline of TicketCredit Tiles to be visible
          }}
        >
          <Flex
            margin="none" // Allow outline of TicketCredit Tiles to be visible
            flexDirection="row"
            flexWrap="wrap"
            gap="md"
          >
            {applicableTicketCredits.slice(2).map((tc) => {
              return (
                <Box
                  key={tc.code}
                  // Force 2 column layout; 50% of container - gap
                  flex="1 1 calc(50% - 16px)"
                  maxWidth="calc(50% - 8px)"
                >
                  <ApplicableTicketCredits.TicketCredit ticketCredit={tc} />
                </Box>
              );
            })}
          </Flex>
        </Collapsible>

        {(hasNextPage || applicableTicketCredits.length > 2) && (
          <Box
            marginLeft="auto"
            marginRight="auto"
            style={{
              // Remove spacing added by the `gap` on the containing flexbox.
              // Because of the Collapse component, it displays a 32px gap.
              // Use `style` for fast performance
              marginTop: isRestOfTicketCreditsExpanded ? "0px" : "-16px",
            }}
          >
            {hasNextPage ? (
              <Button.Ghost
                onClick={() => {
                  fetchNextPage().then(() =>
                    // Wait for the fetch to complete before expanding the collapse
                    setIsRestOfTicketCreditsExpanded(true)
                  );
                }}
                data-testid="load-more-ticket-credits"
                isLoading={isFetchingNextPage}
              >
                {t("Load more")}
              </Button.Ghost>
            ) : (
              <>
                {isRestOfTicketCreditsExpanded ? (
                  <Button.Ghost
                    onClick={() => setIsRestOfTicketCreditsExpanded(false)}
                    data-testid="load-more-ticket-credits"
                    isLoading={isFetchingNextPage}
                    rightIcon={<Icon.ChevronDown width="element.md" />}
                  >
                    {t("View less")}
                  </Button.Ghost>
                ) : (
                  <Button.Ghost
                    onClick={() => setIsRestOfTicketCreditsExpanded(true)}
                    data-testid="load-more-ticket-credits"
                    isLoading={isFetchingNextPage}
                    rightIcon={<Icon.ChevronUp width="element.md" />}
                  >
                    {t("View all")}
                  </Button.Ghost>
                )}
              </>
            )}
          </Box>
        )}
      </Flex>
    </>
  );
}

ApplicableTicketCredits.TicketCredit = function TicketCredit({
  ticketCredit,
}: {
  ticketCredit: UserBasedDiscountTicketInfoSerializer;
}) {
  const { applyDiscount } = usePublicBasket();
  const { t } = useTranslation("checkout");

  const handleClick = () => {
    applyDiscount(String(ticketCredit.code));
  };

  return (
    <Tile.Simple
      outlined
      id={`ticket-credit-${ticketCredit.code}`}
      width="100%"
      height="100%"
      textAlign="left"
      direction="row"
      alignment="center"
      onClick={handleClick}
      trailingElement={
        <Text textDecoration="underline" fontWeight="bold" marginLeft="auto">
          {t("Apply")}
        </Text>
      }
    >
      <Box>
        <Text fontWeight="bold" fontSize="md">
          {ticketCredit.code}
        </Text>
        <Text fontSize="sm" noOfLines={1}>
          {ticketCredit.description}
        </Text>
        <Text fontSize="sm" noOfLines={1}>
          {ticketCredit.stats?.discounts?.used_count}/{ticketCredit?.limit}
        </Text>
      </Box>
    </Tile.Simple>
  );
};
